import { Tooltip } from '@sgme/ui';
import { FormattedMessage } from 'components/shared/Intl';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { ExposedFormStatus } from 'state/forms/models';
import { xlDateTimeFormatOptions } from 'utils/core/date';

import { FormStatusIcon } from '../FormStatusIcon/FormStatusIcon';

export type FormStatusIconTextProps = BaseFormStatusIconProps & {
  quoteResult?: string;
  quoteCreatedAt?: string;
  e2e?: string;
};

export interface BaseFormStatusIconProps {
  status?: ExposedFormStatus;
}

export const FormStatusIconText: FC<FormStatusIconTextProps> = props => {
  const { formatDate, formatMessage } = useIntl();
  const createdAt = !props.quoteCreatedAt
    ? `${formatMessage({ id: 'sidePanel.pricedOn' })} ${formatDate(props.quoteCreatedAt, xlDateTimeFormatOptions)}`
    : undefined;

  return (
    <div
      className={`d-flex align-items-center text-${getTextColorClassFromStatus(props.status)} px-0`}
      data-e2e={props.e2e ?? 'form-status-icon-text'}
    >
      <FormStatusIcon status={props.status} />
      {props.status === 'Expired' ? (
        <div className="col-1 p-0">
          <Tooltip position="bottom" contents={createdAt}>
            <div>{getTextFromStatus(props.status, props.quoteResult)}</div>
          </Tooltip>
        </div>
      ) : (
        getTextFromStatus(props.status, props.quoteResult)
      )}
    </div>
  );
};

function getTextColorClassFromStatus(status?: ExposedFormStatus): string {
  switch (status) {
    case 'NotReady':
    case 'Patching':
    case 'Validating':
      return 'secondary';
    case 'ActionFailed':
    case 'Warning':
      return 'warning';
    case 'Ready':
      return 'info';
    case 'InProgress':
    case 'Duplicating':
      return 'primary';
    case 'Success':
    case 'Expired':
      return '';
    case 'UnknownError':
    case 'Rejected':
      return 'danger';
    default:
      return 'primary';
  }
}

function getTextFromStatus(status?: ExposedFormStatus, quoteResult?: string): ReactNode {
  switch (status) {
    case 'NotReady':
    case 'Patching':
    case 'Validating':
      return <FormattedMessage id="batchPricer.form.status.notReady" />;
    case 'Ready':
      return <FormattedMessage id="batchPricer.form.status.ready" />;
    case 'InProgress':
      return <FormattedMessage id="batchPricer.form.status.inProgress" />;
    case 'Warning':
      return quoteResult ? <span>{quoteResult}</span> : <FormattedMessage id="batchPricer.form.status.warning" />;
    case 'Success':
    case 'Expired':
      return quoteResult ? <span>{quoteResult}</span> : <FormattedMessage id="batchPricer.form.status.success" />;
    case 'UnknownError':
      return <FormattedMessage id="batchPricer.form.status.unknownError" />;
    case 'Rejected':
      return <FormattedMessage id="batchPricer.form.status.rejected" />;
    case 'Duplicating':
      return <FormattedMessage id="batchPricer.form.status.duplicating" />;
    case 'ActionFailed':
      return <FormattedMessage id="batchPricer.form.status.actionFailed" />;
    default:
      return <FormattedMessage id="batchPricer.form.status.loading" />;
  }
}
